import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBriefById, updateBrief } from "../api/api";
import { normalizeBrief } from "../utils/normalizeBrief";
import toast from "react-hot-toast";
import "./Brief.css";
import FormGuidelines from "../components/FormGuidelines";

function getInitials(name = "") {
  return (name || "").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

export default function BriefDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedAnswers, setEditedAnswers] = useState([]);

  useEffect(() => {
    fetchBrief();
  }, [id]);

  const fetchBrief = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getBriefById(id);
      const raw = res?.data?.data ?? res?.data ?? res;
      const data = normalizeBrief(raw);
      setBrief(data);
      setEditedAnswers(data.answers || []);
    } catch {
      setError("تعذر تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await updateBrief(id, { answers: editedAnswers });
      setBrief(prev => ({ ...prev, answers: editedAnswers }));
      setEditing(false);
      toast.success("Saved successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "فشل الحفظ");
    }
  };

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...editedAnswers];
    newAnswers[index] = { ...newAnswers[index], answer: value };
    setEditedAnswers(newAnswers);
  };

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"60vh" }}>
      <div style={{ color:"#4b5563", fontSize:15 }}>جاري التحميل…</div>
    </div>
  );

  if (error) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", gap:12 }}>
      <div style={{ color:"#f87171", fontSize:15 }}>{error}</div>
      <button onClick={fetchBrief} style={{ padding:"10px 24px", background:"#facc15", color:"#0a0f1a", border:"none", borderRadius:8, fontWeight:700, cursor:"pointer" }}>
        إعادة المحاولة
      </button>
    </div>
  );

  if (!brief) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"60vh" }}>
      <div style={{ color:"#4b5563", fontSize:15 }}>Brief not found</div>
    </div>
  );

  return (
    <div className="brief-detail-page">

      {/* Header */}
      <div className="brief-detail-header">
        <button className="back-btn" onClick={() => navigate("/brief")}>
          ← Back
        </button>
        <div className="brief-title-section">
          <h1>Brief Details</h1>
          <div className="brief-client-info">
            <div className="client-avatar-large">{getInitials(brief.name)}</div>
            <div>
              <div className="client-name-large">{brief.name || "—"}</div>
              <div className="client-company-large">{brief.company || "—"}</div>
            </div>
          </div>
        </div>
        <div className="header-actions">
          {editing && (
            <>
              <button className="action-btn cancel" onClick={() => {
                setEditing(false);
                setEditedAnswers(brief.answers || []);
              }}>
                Cancel
              </button>
              <button className="action-btn save" onClick={handleSave}>
                Save
              </button>
            </>
          )}
        </div>
      </div>

      {/* Client Info Card */}
      <div className="brief-info-card">
        <div className="info-row">
          <span className="info-label">Email:</span>
          <span className="info-value">{brief.email}</span>
        </div>
        <div className="info-row">
          <span className="info-label">Phone:</span>
          <span className="info-value">{brief.phone}</span>
        </div>
        <div className="info-row">
          <span className="info-label">Company:</span>
          <span className="info-value">{brief.company || "—"}</span>
        </div>
        {brief.department && (
          <div className="info-row">
            <span className="info-label">Department:</span>
            <span className="info-value">{brief.department}</span>
          </div>
        )}
        <div className="info-row">
          <span className="info-label">Submitted:</span>
          <span className="info-value">{new Date(brief.createdAt).toLocaleString()}</span>
        </div>
      </div>

      {/* Questions & Answers */}
      <div className="brief-answers-section">
        <h2>Questions & Answers</h2>
        {editing && <FormGuidelines />}
        
        {(!editedAnswers || editedAnswers.length === 0) ? (
          <div className="empty-state">
            <p>No answers found.</p>
          </div>
        ) : (
          <div className="answers-list">
            {editedAnswers.map((item, index) => (
              <div key={index} className="answer-item">
                <div className="question-number">Question {index + 1}</div>
                <div className="question-text">{item.question || "No question text"}</div>
                {editing ? (
                  <textarea
                    className="answer-textarea"
                    value={item.answer || ""}
                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                    placeholder="Enter answer..."
                  />
                ) : (
                  <div className="answer-text">{item.answer || "No answer provided"}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
